import { useTranslation } from 'react-i18next'
import {
  Check,
  ListChecks,
  ListOrdered,
  Pencil,
  ToggleLeft,
  Trash2,
} from 'lucide-react'
import type { BankItem, Difficulty, QuestionType } from './types'

type QuestionBankItemCardProps = {
  item: BankItem
  mode: 'manage' | 'picker'
  /** Manage mode — invoked for the caller's own private items only. */
  onEdit?: ((item: BankItem) => void) | undefined
  onDelete?: ((item: BankItem) => void) | undefined
  /** Picker mode — current selection state + toggle. */
  selected?: boolean | undefined
  onToggleSelect?: ((item: BankItem) => void) | undefined
}

const TYPE_META: Record<
  QuestionType,
  { icon: typeof ListChecks; className: string; labelKey: 'mcqBadge' | 'tfBadge' | 'orderBadge' }
> = {
  MultipleChoice: {
    icon: ListChecks,
    className: 'bg-brand-50 text-brand-700',
    labelKey: 'mcqBadge',
  },
  TrueFalse: {
    icon: ToggleLeft,
    className: 'bg-emerald-50 text-emerald-700',
    labelKey: 'tfBadge',
  },
  OrderSequence: {
    icon: ListOrdered,
    className: 'bg-amber-50 text-amber-700',
    labelKey: 'orderBadge',
  },
}

const DIFFICULTY_META: Record<
  Difficulty,
  { className: string; labelKey: 'bankDifficultyEasy' | 'bankDifficultyMedium' | 'bankDifficultyHard' }
> = {
  Easy: { className: 'bg-emerald-50 text-emerald-700', labelKey: 'bankDifficultyEasy' },
  Medium: { className: 'bg-amber-50 text-amber-700', labelKey: 'bankDifficultyMedium' },
  Hard: { className: 'bg-red-50 text-red-700', labelKey: 'bankDifficultyHard' },
}

/**
 * One question-bank item rendered as a card. Mirrors the dashboard quiz-card
 * vocabulary (`rounded-2xl border border-brand-100 border-s-4 border-s-brand-500`).
 * In `manage` mode the caller's own private items expose edit/delete; in
 * `picker` mode the whole card is a checkbox label for multi-select.
 */
export function QuestionBankItemCard({
  item,
  mode,
  onEdit,
  onDelete,
  selected = false,
  onToggleSelect,
}: QuestionBankItemCardProps) {
  const { t } = useTranslation('quiz')
  const typeMeta = TYPE_META[item.type]
  const TypeIcon = typeMeta.icon

  const meta = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${typeMeta.className}`}
        >
          <TypeIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {t(typeMeta.labelKey)}
        </span>
        <span
          className={
            'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ' +
            (item.scope === 'Public'
              ? 'bg-brand-100 text-brand-700'
              : 'bg-violet-50 text-violet-700')
          }
        >
          {item.scope === 'Public' ? t('bankScopePublic') : t('bankScopePrivate')}
        </span>
        {item.difficulty ? (
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${DIFFICULTY_META[item.difficulty].className}`}
          >
            {t(DIFFICULTY_META[item.difficulty].labelKey)}
          </span>
        ) : null}
      </div>

      <p className="mt-3 text-sm font-semibold text-brand-900 leading-relaxed line-clamp-3">
        {item.text}
      </p>

      <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
        {item.categoryName ?? t('bankUncategorized')}
      </span>
    </>
  )

  if (mode === 'picker') {
    return (
      <label
        className={
          'group relative flex h-full cursor-pointer flex-col rounded-2xl border bg-white border-s-4 ps-5 pe-5 py-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ' +
          (selected
            ? 'border-brand-300 border-s-brand-600 ring-2 ring-brand-200'
            : 'border-brand-100 border-s-brand-500 hover:border-brand-200')
        }
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="text-xs font-semibold text-brand-500">
            {selected ? t('bankPickerSelected') : t('bankPickerSelect')}
          </span>
          <span
            aria-hidden="true"
            className={
              'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ' +
              (selected
                ? 'border-brand-500 bg-brand-500 text-white'
                : 'border-brand-300 bg-white text-transparent')
            }
          >
            <Check className="h-3.5 w-3.5" strokeWidth={3} />
          </span>
        </div>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect?.(item)}
          className="sr-only"
        />
        {meta}
      </label>
    )
  }

  return (
    <article className="group relative flex h-full flex-col rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-5 pe-5 py-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl hover:border-brand-200 hover:border-s-brand-600">
      {meta}

      {item.isOwnedByMe ? (
        <div className="mt-auto flex items-center justify-end gap-1 pt-4">
          <button
            type="button"
            onClick={() => onEdit?.(item)}
            aria-label={t('bankEditItem')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-brand-600 hover:bg-brand-50 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(item)}
            aria-label={t('bankDeleteItem')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-red-600 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </article>
  )
}
